import React, { useState, useEffect, useMemo } from 'react';
import { message, Empty } from 'antd';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { AnalysisFilterBar, SellerMapping } from './components/AnalysisFilterBar';
import { AnalysisStats } from './components/AnalysisStats';
import { AnalysisCharts } from './components/AnalysisCharts';

interface Props {
    skuId: number;
    sellerSkuId?: number; // Deprecated, kept for backward compatibility
    sellerMappings?: SellerMapping[];
}

const SKUSalesAnalysis: React.FC<Props> = ({ skuId, sellerSkuId, sellerMappings = [] }) => {
    // Determine initial selected ID
    const initialId = sellerMappings.length > 0 ? sellerMappings[0].id : sellerSkuId;
    const [selectedSellerSkuId, setSelectedSellerSkuId] = useState<number | undefined>(initialId);

    const [trendData, setTrendData] = useState<any[]>([]);
    const [distData, setDistData] = useState<any>(null);
    const [geoData, setGeoData] = useState<any>(null);

    const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([dayjs().subtract(30, 'd'), dayjs()]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');

    // Filters
    const [country, setCountry] = useState<string>('US');
    const [salesChannel, setSalesChannel] = useState<string>('Amazon.com');

    // Find current mapping object for display
    const currentMapping = sellerMappings.find(m => m.id === selectedSellerSkuId);

    useEffect(() => {
        if (!selectedSellerSkuId && sellerMappings.length > 0) {
            setSelectedSellerSkuId(sellerMappings[0].id);
        } else if (!selectedSellerSkuId && sellerSkuId) {
            setSelectedSellerSkuId(sellerSkuId);
        }
    }, [sellerMappings, sellerSkuId]);

    const loadAnalysis = async () => {
        if (!dates || !dates[0] || !dates[1]) { return message.warning('请选择日期范围'); }
        if (!selectedSellerSkuId) return;

        setAnalysisLoading(true);
        try {
            const commonParams = {
                start_date: dates[0].format('YYYY-MM-DD'), end_date: dates[1].format('YYYY-MM-DD'),
                target_ids: String(selectedSellerSkuId), dimension: 'seller_sku',
                country: country || undefined, sales_channel: salesChannel || undefined,
            };

            const [trendRes, distRes, geoRes] = await Promise.all([
                api.get('/data-analysis/sales-trend/', { params: { ...commonParams, granularity } }),
                api.get('/data-analysis/time-distribution/', { params: { ...commonParams } }),
                api.get('/data-analysis/geographic-distribution/', { params: { ...commonParams, country: 'US' } })
            ]);

            setTrendData(trendRes.data);
            setDistData(distRes.data);
            setGeoData(geoRes.data);
            message.success('分析数据已更新');
        } catch (error) { console.error(error); message.error('加载分析数据失败'); }
        finally { setAnalysisLoading(false); }
    };

    useEffect(() => { if (selectedSellerSkuId) loadAnalysis(); }, [selectedSellerSkuId]);

    const summary = useMemo(() => {
        let totalSales = 0, totalUnits = 0, totalOrders = 0;
        trendData.forEach(item => {
            totalSales += Number(item.sales || 0);
            totalUnits += Number(item.units || 0);
            totalOrders += Number(item.order_items || 0);
        });
        const aov = totalOrders > 0 ? totalSales / totalOrders : 0;
        return { totalSales, totalUnits, totalOrders, aov };
    }, [trendData]);

    if (!selectedSellerSkuId) {
        return <Empty description="未找到关联的销售SKU，请先在SKU映射中关联，或确认映射已生效。" />;
    }

    return (
        <div style={{ padding: '0 8px' }}>
            <AnalysisFilterBar
                sellerMappings={sellerMappings}
                selectedSellerSkuId={selectedSellerSkuId}
                onSelectedSellerSkuIdChange={setSelectedSellerSkuId}
                dates={dates} onDatesChange={setDates}
                country={country} onCountryChange={setCountry}
                salesChannel={salesChannel} onSalesChannelChange={setSalesChannel}
                granularity={granularity} onGranularityChange={setGranularity}
                onRefresh={loadAnalysis} loading={analysisLoading}
                currentMapping={currentMapping}
            />
            <AnalysisStats summary={summary} />
            <AnalysisCharts trendData={trendData} distData={distData} geoData={geoData} granularity={granularity} />
        </div>
    );
};

export default SKUSalesAnalysis;
